import React, { useEffect, useMemo, useState } from 'react';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';

import { Alerta } from '../../components/alerta';
import MultiSelectFilter from '../../components/multiSelectFilter';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatCurrency } from '../../utils/stringFormat';

const ALL_PROJETOS_OPTION = 'all';
const ALL_ANOS_OPTION = 'all';
const ANO_REGEX = /20\d{2}/g;

const sumValues = (values) => values.reduce((acc, value) => acc + value, 0);

const formatCell = (value) => (value === 0 ? '' : formatCurrency(value));

const valueColor = (value) => (value < 0 ? 'error.main' : 'var(--app-text)');

const cellSx = {
    borderColor: 'var(--app-line)',
    color: 'var(--app-text)',
    fontFamily: 'var(--app-font-body)',
    whiteSpace: 'nowrap',
    py: 1
};

const headCellSx = {
    ...cellSx,
    fontWeight: 700,
    color: 'var(--app-muted)',
    textTransform: 'uppercase',
    fontSize: '.72rem',
    letterSpacing: '.04em',
    backgroundColor: 'var(--app-surface-strong)'
};

const ValueCell = ({ value, bold = false }) => (
    <TableCell align='right' sx={{ ...cellSx, color: valueColor(value), fontWeight: bold ? 700 : 500 }}>
        {formatCell(value)}
    </TableCell>
);

const ResumoCard = ({ label, value }) => (
    <Paper variant='outlined' sx={{ p: 2, borderRadius: 3, flex: 1, minWidth: 0, borderColor: 'var(--app-line)', backgroundColor: 'var(--app-surface)' }}>
        <Typography sx={{ color: 'var(--app-muted)', fontSize: '.78rem', fontWeight: 600 }}>
            {label}
        </Typography>
        <Typography sx={{ color: valueColor(value), fontFamily: 'var(--app-font-display)', fontSize: '1.35rem', fontWeight: 700 }}>
            {formatCurrency(value)}
        </Typography>
    </Paper>
);

const DreTable = ({ dre }) => {
    const [collapsedTipos, setCollapsedTipos] = useState([]);
    const hasTotalColumn = dre.projetos.length > 1;

    const tipos = useMemo(() => dre.tipos.map((tipo) => ({
        ...tipo,
        subtotais: dre.projetos.map((projeto) =>
            sumValues(tipo.categorias.map((categoria) => categoria.valores[projeto.codigo]))
        )
    })), [dre]);

    const totaisGerais = dre.projetos.map((_, index) =>
        sumValues(tipos.map((tipo) => tipo.subtotais[index]))
    );

    const allCollapsed = collapsedTipos.length === tipos.length;

    function toggleTipo (nomeTipo) {
        setCollapsedTipos((prev) => (
            prev.includes(nomeTipo) ? prev.filter((item) => item !== nomeTipo) : [...prev, nomeTipo]
        ));
    }

    function toggleAll () {
        setCollapsedTipos(allCollapsed ? [] : tipos.map((tipo) => tipo.tipo));
    }

    return (
        <Stack spacing={1.5}>
            <Box display='flex' justifyContent='flex-end'>
                <Button
                    type='button'
                    size='small'
                    onClick={toggleAll}
                    startIcon={allCollapsed ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowRightRoundedIcon />}
                >
                    {allCollapsed ? 'Expandir tudo' : 'Recolher tudo'}
                </Button>
            </Box>

            <TableContainer component={Paper} variant='outlined' sx={{ borderRadius: 0, borderColor: 'var(--app-line)', backgroundColor: 'var(--app-surface)' }}>
                <Table size='small' stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headCellSx}>Tipo</TableCell>
                            <TableCell sx={headCellSx}>Categoria</TableCell>
                            {dre.projetos.map((projeto) => (
                                <TableCell key={projeto.codigo} align='right' sx={headCellSx}>
                                    {projeto.nome}
                                </TableCell>
                            ))}
                            {hasTotalColumn && <TableCell align='right' sx={headCellSx}>Total</TableCell>}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {tipos.map((tipo) => {
                            const isCollapsed = collapsedTipos.includes(tipo.tipo);

                            return (
                                <React.Fragment key={tipo.tipo}>
                                    <TableRow
                                        hover
                                        onClick={() => toggleTipo(tipo.tipo)}
                                        sx={{ cursor: 'pointer', backgroundColor: 'var(--app-surface-strong)' }}
                                    >
                                        <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                                            <Box display='flex' alignItems='center' gap={0.5}>
                                                <IconButton size='small' sx={{ color: 'var(--app-muted)', p: 0.25 }}>
                                                    {isCollapsed ? <KeyboardArrowRightRoundedIcon fontSize='small' /> : <KeyboardArrowDownRoundedIcon fontSize='small' />}
                                                </IconButton>
                                                {tipo.tipo}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ ...cellSx, color: 'var(--app-muted)' }}>
                                            {tipo.categorias.length} categorias
                                        </TableCell>
                                        {tipo.subtotais.map((subtotal, index) => (
                                            <ValueCell key={dre.projetos[index].codigo} value={subtotal} bold />
                                        ))}
                                        {hasTotalColumn && <ValueCell value={sumValues(tipo.subtotais)} bold />}
                                    </TableRow>

                                    {!isCollapsed && tipo.categorias.map((categoria) => {
                                        const valores = dre.projetos.map((projeto) => categoria.valores[projeto.codigo]);

                                        return (
                                            <TableRow key={`${tipo.tipo}-${categoria.categoria}`} hover>
                                                <TableCell sx={cellSx} />
                                                <TableCell sx={cellSx}>{categoria.categoria}</TableCell>
                                                {valores.map((valor, index) => (
                                                    <ValueCell key={dre.projetos[index].codigo} value={valor} />
                                                ))}
                                                {hasTotalColumn && <ValueCell value={sumValues(valores)} />}
                                            </TableRow>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}

                        <TableRow sx={{ backgroundColor: 'var(--app-surface-strong)' }}>
                            <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Total geral</TableCell>
                            <TableCell sx={cellSx} />
                            {totaisGerais.map((total, index) => (
                                <ValueCell key={dre.projetos[index].codigo} value={total} bold />
                            ))}
                            {hasTotalColumn && <ValueCell value={sumValues(totaisGerais)} bold />}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
};

const DreProjetos = () => {
    const [projetos, setProjetos] = useState([]);
    const [selectedAnos, setSelectedAnos] = useState([ALL_ANOS_OPTION]);
    const [selectedProjetos, setSelectedProjetos] = useState([ALL_PROJETOS_OPTION]);
    const [dre, setDre] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmit, setIsSubmit] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    useEffect(() => {
        async function carregarProjetos () {
            setIsLoading(true);
            const response = await fetchFlask('listarProjetosDRE');

            if (response.status === 'success') {
                setProjetos(response.message);
            } else {
                handleAlerta(response.message || 'Nao foi possivel carregar os projetos.', response.status || 'error');
            }

            setIsLoading(false);
        }

        carregarProjetos();
    }, []);

    const anoOptions = useMemo(() => {
        const anos = [...new Set(projetos.flatMap((projeto) => projeto.nome.match(ANO_REGEX) || []))].sort();

        return [
            { value: ALL_ANOS_OPTION, label: 'Todos os anos' },
            ...anos.map((ano) => ({ value: ano, label: ano }))
        ];
    }, [projetos]);

    const projetosFiltrados = useMemo(() => (
        selectedAnos.includes(ALL_ANOS_OPTION)
            ? projetos
            : projetos.filter((projeto) => selectedAnos.some((ano) => projeto.nome.includes(ano)))
    ), [projetos, selectedAnos]);

    const projetoOptions = useMemo(() => [
        { value: ALL_PROJETOS_OPTION, label: 'Todos os projetos' },
        ...projetosFiltrados.map((projeto) => ({ value: projeto.codigo, label: projeto.nome }))
    ], [projetosFiltrados]);

    const resumo = useMemo(() => {
        if (!dre) {
            return null;
        }

        const valores = dre.tipos.flatMap((tipo) =>
            tipo.categorias.flatMap((categoria) =>
                dre.projetos.map((projeto) => categoria.valores[projeto.codigo])
            )
        );
        const positivo = sumValues(valores.filter((valor) => valor > 0));
        const negativo = sumValues(valores.filter((valor) => valor < 0));

        return { positivo, negativo, resultado: positivo + negativo };
    }, [dre]);

    function handleChangeAnos (anos) {
        setSelectedAnos(anos);
        setSelectedProjetos([ALL_PROJETOS_OPTION]);
    }

    function handleAlerta (texto, novoStatus) {
        setMessage(texto);
        setStatus(novoStatus);
        setOpenAlerta(true);
    }

    async function submitGerarDRE (e) {
        e.preventDefault();
        setIsSubmit(true);

        const codigos = selectedProjetos.includes(ALL_PROJETOS_OPTION)
            ? projetosFiltrados.map((projeto) => projeto.codigo)
            : selectedProjetos;

        const response = await fetchFlask('gerarDRE', JSON.stringify({ projetos: codigos }));

        if (response.status === 'success') {
            setDre(response.message);
        } else {
            setDre(null);
            handleAlerta(response.message, response.status);
        }

        setIsSubmit(false);
    }

    return (
        <Box className='mainBox'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />

            <PageHeader>DRE por Projeto</PageHeader>

            <Box sx={{ width: '100%', maxWidth: 1440, mx: 'auto' }}>
                <Stack spacing={3}>
                    <form onSubmit={submitGerarDRE}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                            <Box sx={{ width: { xs: '100%', md: 200 }, '& .MuiFormControl-root': { width: '100%' } }}>
                                <MultiSelectFilter
                                    labelId='dre-anos-filter-label'
                                    label='Ano'
                                    value={selectedAnos}
                                    options={anoOptions}
                                    allOptionValue={ALL_ANOS_OPTION}
                                    disabled={isLoading || projetos.length === 0}
                                    onChange={handleChangeAnos}
                                />
                            </Box>

                            <Box sx={{ width: { xs: '100%', md: 420 }, '& .MuiFormControl-root': { width: '100%' } }}>
                                <MultiSelectFilter
                                    labelId='dre-projetos-filter-label'
                                    label='Projetos'
                                    value={selectedProjetos}
                                    options={projetoOptions}
                                    allOptionValue={ALL_PROJETOS_OPTION}
                                    disabled={isLoading || projetosFiltrados.length === 0}
                                    onChange={setSelectedProjetos}
                                />
                            </Box>

                            <Box>
                                <SubmitButton
                                    isSubmit={isSubmit}
                                    disabled={isLoading || projetosFiltrados.length === 0}
                                    startIcon={<ReceiptLongRoundedIcon />}
                                >
                                    Gerar DRE
                                </SubmitButton>
                            </Box>
                        </Stack>
                    </form>

                    {isLoading && (
                        <Typography color='text.secondary'>
                            Carregando projetos...
                        </Typography>
                    )}

                    {!isLoading && projetos.length === 0 && (
                        <Typography color='text.secondary'>
                            Nenhum projeto disponivel para selecao.
                        </Typography>
                    )}

                    {dre && (
                        <>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <ResumoCard label='Total positivo' value={resumo.positivo} />
                                <ResumoCard label='Total negativo' value={resumo.negativo} />
                                <ResumoCard label='Resultado' value={resumo.resultado} />
                            </Stack>

                            <DreTable key={dre.projetos.map((projeto) => projeto.codigo).join(',')} dre={dre} />
                        </>
                    )}
                </Stack>
            </Box>
        </Box>
    );
};

export default DreProjetos;
